import { Container, Graphics } from '../../../vendor/pixi.min.mjs';

/**
 * Slow infinite "breathing" pulse for the station sprite — runs at all
 * times so the player can tell at a glance that the station is the
 * thing they're supposed to drive to. Kept very subtle (±4 % of full
 * size, ~1.6 s period) so it reads as "alive" without being
 * distracting.
 */
const STATION_PULSE_AMP = 0.04;
const STATION_PULSE_FREQ = (2 * Math.PI) / 1600; // 1.6 s full cycle

/**
 * Station pickup manager for Snake station mode.
 * Spawns a single station and returns passenger count on pickup.
 *
 * Each spawned station's visual is wrapped in a per-station Container
 * (`st._wrap`) so the pulse logic can scale the visual around its own
 * centre without disturbing anything else in `this.container`.
 */
export class SnakeStations {
    container = new Container();

    _stations = [];
    _cells = new Set();
    _dirty = true;
    _lastOx = 0;
    _lastOy = 0;
    _lastCs = 0;
    _themeManager = null;

    constructor(themeManager = null) {
        this._themeManager = themeManager;
    }

    spawn(cols, rows, snake, obstacles = null, perks = null, isBlocked = null, isReachable = null) {
        this.clearAll();

        // Stations are ALWAYS the same physical footprint (4×2 = 200×100 px,
        // big enough for the artwork to read cleanly without dwarfing the
        // playfield). Only the variant + passenger count varies per roll:
        //   • single → 1 passenger → snake grows by 1 full tram cart (2 cells)
        //   • double → 2 passengers → snake grows by 2 full tram carts (4 cells)
        // The PNG asset (`tram-station-single.png` or `tram-station-double.png`)
        // is picked by `variant` in render().
        const shapes = [
            { w: 4, h: 2, passengers: 1, variant: 'single' },
            { w: 4, h: 2, passengers: 2, variant: 'double' },
        ];

        const candidates = [];
        for (const shape of shapes) {
            for (let col = 0; col <= cols - shape.w; col++) {
                for (let row = 0; row <= rows - shape.h; row++) {
                    let fits = true;
                    for (let dc = 0; dc < shape.w && fits; dc++) {
                        for (let dr = 0; dr < shape.h && fits; dr++) {
                            const c = col + dc;
                            const r = row + dr;
                            if (
                                snake.occupies(c, r) ||
                                obstacles?.occupies(c, r) ||
                                perks?.occupies(c, r) ||
                                isBlocked?.(c, r)
                            ) {
                                fits = false;
                            }
                        }
                    }
                    if (fits) {
                        candidates.push({ col, row, ...shape });
                    }
                }
            }
        }

        if (!candidates.length) return;

        // Reachability filter: only keep stations whose perimeter contains
        // at least one cell the snake can actually walk into. Without this
        // a station may spawn fully surrounded by buildings — visible but
        // unreachable. Falls back to the un-filtered candidate list when
        // nothing passes (avoids "no station at all" failure modes on
        // tightly packed boards).
        let usable = candidates;
        if (typeof isReachable === 'function') {
            const reachable = candidates.filter(c =>
                _hasReachablePerimeter(c, cols, rows, isReachable),
            );
            if (reachable.length) usable = reachable;
        }

        const pick = usable[Math.floor(Math.random() * usable.length)];
        this._stations.push(pick);
        for (let dc = 0; dc < pick.w; dc++) {
            for (let dr = 0; dr < pick.h; dr++) {
                this._cells.add(`${pick.col + dc},${pick.row + dr}`);
            }
        }
        this._dirty = true;
    }

    occupies(col, row) {
        return this._cells.has(`${col},${row}`);
    }

    checkCollection(col, row) {
        for (let i = 0; i < this._stations.length; i++) {
            const st = this._stations[i];
            if (col >= st.col && col < st.col + st.w && row >= st.row && row < st.row + st.h) {
                const passengers = st.passengers;
                this.clearAll();
                return passengers;
            }
        }
        return 0;
    }

    render(originX, originY, cellSize) {
        if (!this._dirty && originX === this._lastOx && originY === this._lastOy && cellSize === this._lastCs) {
            return;
        }
        this._dirty = false;
        this._lastOx = originX;
        this._lastOy = originY;
        this._lastCs = cellSize;

        // Drop any wrapper references that pointed at the previous
        // render — they'll be re-created below. Children are torn down
        // via removeChildren so we don't leak texture references.
        for (const st of this._stations) {
            st._wrap = null;
        }
        this.container.removeChildren();

        for (const st of this._stations) {
            const w = st.w * cellSize;
            const h = st.h * cellSize;
            const cx = originX + st.col * cellSize + w / 2;
            const cy = originY + st.row * cellSize + h / 2;

            // Per-station wrapper at the station's world centre. Its
            // contents are drawn in LOCAL coords centred around (0, 0)
            // so {@link pulse} can scale the wrapper without warping
            // its position on the board.
            const wrap = new Container();
            wrap.x = cx;
            wrap.y = cy;

            // Variant chosen by the spawner picks single vs double-platform art.
            // Old `wide`/`tall` slots are retained in the theme config purely
            // as customisation hooks; the spawner no longer emits them.
            const slotKey = st.variant === 'double' ? 'snake.station.wide' : 'snake.station.single';
            const sprite = this._themeManager?.createSprite(slotKey, w, h, { fitMode: 'contain' });

            if (sprite) {
                // Sprite is already anchor-0.5 so positioning at the
                // wrapper origin centres it perfectly.
                sprite.x = 0;
                sprite.y = 0;
                wrap.addChild(sprite);
            } else {
                // Graphics fallback — draw in local coords (top-left
                // of the station = (-w/2, -h/2)) so the wrapper's
                // origin sits at the visual centre.
                const g = new Graphics();
                const lx = -w / 2;
                const ly = -h / 2;

                g.roundRect(lx + w * 0.08, ly + h * 0.78, w * 0.84, h * 0.12, 4);
                g.fill(0x6b7280);
                g.roundRect(lx + w * 0.08, ly + h * 0.78, w * 0.84, h * 0.04, 4);
                g.fill(0xfbbf24);

                const vertical = st.h > st.w;
                if (vertical) {
                    g.roundRect(lx + w * 0.18, ly + h * 0.14, w * 0.64, h * 0.12, 4);
                    g.fill(0x38bdf8);
                    g.rect(lx + w * 0.26, ly + h * 0.24, w * 0.08, h * 0.42);
                    g.fill(0x94a3b8);
                    g.rect(lx + w * 0.66, ly + h * 0.24, w * 0.08, h * 0.42);
                    g.fill(0x94a3b8);
                    g.roundRect(lx + w * 0.22, ly + h * 0.28, w * 0.56, h * 0.26, 4);
                    g.fill({ color: 0x7dd3fc, alpha: 0.35 });
                } else {
                    g.roundRect(lx + w * 0.12, ly + h * 0.18, w * 0.76, h * 0.14, 4);
                    g.fill(0x38bdf8);
                    g.rect(lx + w * 0.2, ly + h * 0.3, w * 0.05, h * 0.32);
                    g.fill(0x94a3b8);
                    g.rect(lx + w * 0.75, ly + h * 0.3, w * 0.05, h * 0.32);
                    g.fill(0x94a3b8);
                    g.roundRect(lx + w * 0.24, ly + h * 0.34, w * 0.52, h * 0.18, 4);
                    g.fill({ color: 0x7dd3fc, alpha: 0.35 });
                }

                g.rect(lx + w * 0.16, ly + h * 0.7, w * 0.68, h * 0.02);
                g.fill(0xe5e7eb);

                const people = st.passengers;
                const baseY = ly + h * 0.6;
                for (let i = 0; i < people; i++) {
                    const px = lx + w * (people === 1 ? 0.5 : 0.36 + i * 0.28);
                    const headR = Math.max(3, cellSize * 0.07);
                    g.circle(px, baseY - headR * 1.8, headR);
                    g.fill(0xf3d2b6);
                    g.roundRect(px - headR * 0.75, baseY - headR * 0.55, headR * 1.5, headR * 1.9, 3);
                    g.fill(i % 2 === 0 ? 0x16a34a : 0xef4444);
                    g.rect(px - headR * 0.55, baseY + headR * 1.35, headR * 0.35, headR * 0.95);
                    g.fill(0x334155);
                    g.rect(px + headR * 0.2, baseY + headR * 1.35, headR * 0.35, headR * 0.95);
                    g.fill(0x334155);
                }

                g.roundRect(lx + w * 0.2, ly + h * 0.64, w * 0.16, h * 0.06, 3);
                g.fill(0x92400e);

                g.roundRect(lx + w * 0.12, ly + h * 0.08, w * 0.28, h * 0.1, 4);
                g.fill(0x2563eb);
                g.roundRect(lx + w * 0.15, ly + h * 0.11, w * 0.22, h * 0.04, 3);
                g.fill(0xe0f2fe);

                wrap.addChild(g);
            }

            st._wrap = wrap;
            this.container.addChild(wrap);
        }
    }

    /**
     * Drive the slow infinite "breathing" pulse on every active
     * station. Call once per frame. Safe to call before any station
     * exists / has been rendered — it's a no-op.
     *
     * The amplitude is small on purpose: the pulse is a passive
     * "this thing is alive, come get it" cue and should not compete
     * with active pickup confetti or the board intro animation.
     */
    pulse() {
        if (this._stations.length === 0) return;
        const now = Date.now();
        const s = 1.0 + STATION_PULSE_AMP * Math.sin(now * STATION_PULSE_FREQ);
        for (const st of this._stations) {
            const w = st._wrap;
            if (w && !w.destroyed) w.scale.set(s);
        }
    }

    clearAll() {
        for (const st of this._stations) {
            st._wrap = null;
        }
        this._stations = [];
        this._cells.clear();
        this.container.removeChildren();
        this._dirty = true;
    }

    destroy() {
        this.clearAll();
    }
}

/**
 * Returns true if at least one in-bounds perimeter cell of the given
 * station candidate is reachable per the supplied predicate. The
 * perimeter is the ring of cells immediately surrounding the station's
 * footprint (top row, bottom row, left column, right column — corners
 * included). The snake only needs ONE accessible side to reach the
 * station so a single hit is sufficient.
 */
function _hasReachablePerimeter(cand, cols, rows, isReachable) {
    const { col, row, w, h } = cand;
    // Top + bottom rows (corners included).
    for (let c = col - 1; c <= col + w; c++) {
        if (c < 0 || c >= cols) continue;
        if (row - 1 >= 0     && isReachable(c, row - 1)) return true;
        if (row + h < rows   && isReachable(c, row + h)) return true;
    }
    // Left + right columns (exclude corners — already checked above).
    for (let r = row; r < row + h; r++) {
        if (r < 0 || r >= rows) continue;
        if (col - 1 >= 0     && isReachable(col - 1, r)) return true;
        if (col + w < cols   && isReachable(col + w, r)) return true;
    }
    return false;
}
